import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ip_back } from '../config';
import { usePermissions } from '../context/Permissioncontext';
import { useBrand } from '../context/BrandContext';
import { syncBrandAfterAuth } from '../utils/brandAuth';
import { HOME_ACTIVE_ROLE_KEY } from '../utils/homeRole';
import { applyClubAdminUiLanguage } from '../utils/clubCountryLanguage';
import { completeClubAdminRegistration } from '../utils/completeClubAdminRegistration';
import { LegalAcceptCheckbox } from '../components/legal/LegalAcceptCheckbox';
import { LegalFooter } from '../components/legal/LegalFooter';
import { recordPrivacyAndTermsAcceptance } from '../api/socialClipApi';
import './PasswordRegistration.css';

function mergeClubAdminUser(user, profiling, clubId) {
  const selected = profiling?.selectedClub || {};
  const club_details = {
    ...(user?.club_details || {}),
    club_id: clubId || user?.club_id || selected.club_id || user?.club_details?.club_id,
    club_name:
      user?.club_details?.club_name ||
      user?.club_name ||
      selected.club_name ||
      profiling?.clubName,
    country:
      user?.club_details?.country ||
      selected.country ||
      selected.club_country ||
      profiling?.clubCountryCode ||
      profiling?.clubCountry,
    club_description:
      user?.club_details?.club_description || selected.club_description,
    club_code: user?.club_details?.club_code || selected.club_code,
    city:
      user?.club_details?.city ||
      selected.city ||
      selected.club_city ||
      profiling?.clubCity,
    postal_code: user?.club_details?.postal_code || selected.postal_code,
  };
  return {
    ...user,
    club_id: club_details.club_id || user?.club_id,
    club_name: club_details.club_name || user?.club_name,
    club_details,
  };
}

const PasswordRegistration = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { loadPermissions } = usePermissions();
  const { refreshBrand } = useBrand();
  
  const [formData, setFormData] = useState({
    password: '',
    passwordConfirmation: '',
    legalAccepted: false,
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userType, setUserType] = useState('player'); // Default to player

  useEffect(() => {
    console.log('🔍 PasswordRegistration: Checking email confirmation token...');
    
    // Check if email confirmation token exists
    const emailConfirmationToken = localStorage.getItem('emailConfirmationToken');
    const pendingEmail = localStorage.getItem('pendingEmail');
    
    console.log('🔍 Token exists:', !!emailConfirmationToken);
    console.log('🔍 Pending email:', pendingEmail);
    console.log('🔍 All localStorage keys:', Object.keys(localStorage));
    
    // Check if this is player registration from profiling (direct flow - no email link required yet)
    const profilingData = localStorage.getItem('profilingFormData');
    if (profilingData) {
      try {
        const parsedProfilingData = JSON.parse(profilingData);
        console.log('🏢 Profiling data found:', parsedProfilingData);
        
        if (parsedProfilingData.userType === 'club_admin') {
          setUserType('club_admin');
          console.log('🏢 Detected club admin registration from profiling data');
          const adminEmail = parsedProfilingData.adminEmail || pendingEmail;
          if (parsedProfilingData.invitationToken && adminEmail) {
            if (!pendingEmail) {
              localStorage.setItem('pendingEmail', adminEmail);
            }
            setUserEmail(adminEmail);
            setEmailConfirmed(true);
            return;
          }
        }
        // Allow access for new player coming from profiling even without email token
        if (parsedProfilingData.userType === 'player' || localStorage.getItem('selectedUserType') === 'player') {
          const emailFromProfiling = parsedProfilingData.email || pendingEmail;
          if (emailFromProfiling) {
            console.log('✅ Player from profiling - allowing access with email from form:', emailFromProfiling);
            if (!pendingEmail) {
              localStorage.setItem('pendingEmail', emailFromProfiling);
            }
            setUserEmail(emailFromProfiling);
            setUserType('player');
            setEmailConfirmed(true);
            return;
          }
        }
      } catch (error) {
        console.log('❌ Error parsing profiling data:', error);
      }
    }
    
    if (!emailConfirmationToken) {
      console.log('❌ No email confirmation token found, redirecting to registration');
      // No token, redirect to registration
      navigate('/register');
      return;
    }
    
    // If we have a token but no pending email, try to decode it
    if (emailConfirmationToken && !pendingEmail) {
      try {
        const tokenParts = emailConfirmationToken.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          if (payload.email) {
            localStorage.setItem('pendingEmail', payload.email);
            setUserEmail(payload.email);
            console.log('✅ Extracted email from token:', payload.email);
          }
          
          // Check if this is a club admin registration by looking for club data in the token
          if (payload.clubData || payload.isClubAdmin) {
            console.log('🏢 Club admin email confirmation detected from token');
            console.log('🏢 Club data:', payload.clubData);
            setUserType('club_admin');
          }
        }
      } catch (error) {
        console.log('❌ Could not decode token:', error);
        navigate('/register');
        return;
      }
    } else if (pendingEmail) {
      setUserEmail(pendingEmail);
      console.log('✅ Using stored email:', pendingEmail);
    }
    
    setEmailConfirmed(true);
    console.log('✅ Email confirmation validated successfully');
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    if (!formData.passwordConfirmation) {
      newErrors.passwordConfirmation = 'Please confirm your password';
    } else if (formData.password !== formData.passwordConfirmation) {
      newErrors.passwordConfirmation = 'Passwords do not match';
    }

    if (!formData.legalAccepted) {
      newErrors.legalAccepted = t('legalAcceptRequired', 'Please confirm you have read the privacy notice and accept the terms of use');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Get the email confirmation token
      const emailConfirmationToken = localStorage.getItem('emailConfirmationToken');
      const email = localStorage.getItem('pendingEmail');
      
      console.log('🔍 PasswordRegistration handleSubmit:');
      console.log('🔍 Token exists:', !!emailConfirmationToken);
      console.log('🔍 Email exists:', !!email);
      console.log('🔍 Email value:', email);
      console.log('🔍 All localStorage keys:', Object.keys(localStorage));
      
      // Get profiling data first - needed for both club admin and direct player registration
      const profilingData = localStorage.getItem('profilingFormData');
      let parsedProfilingData = null;
      
      console.log('🔍 Raw profiling data from localStorage:', profilingData);
      console.log('🔍 All localStorage keys:', Object.keys(localStorage));
      
      if (profilingData) {
        try {
          parsedProfilingData = JSON.parse(profilingData);
          console.log('🏢 Profiling data found:', parsedProfilingData);
          console.log('🏢 User type from profiling data:', parsedProfilingData.userType);
          console.log('🏢 Will set typeUser_id to:', parsedProfilingData?.userType === 'club_admin' ? 2 : 4);
          console.log('🏢 Selected club:', parsedProfilingData.selectedClub);
          console.log('🏢 Club name:', parsedProfilingData.clubName);
          console.log('🏢 Admin first name:', parsedProfilingData.adminFirstName);
          console.log('🏢 Admin last name:', parsedProfilingData.adminLastName);
          console.log('🏢 Admin position:', parsedProfilingData.adminPosition);
        } catch (error) {
          console.log('❌ Error parsing profiling data:', error);
        }
      } else {
        console.log('❌ No profiling data found in localStorage');
      }
      
      // Direct player registration: has profiling form with email but no token (came from profiling page)
      const isDirectPlayerRegistration = (parsedProfilingData?.userType === 'player' || localStorage.getItem('selectedUserType') === 'player') &&
        (parsedProfilingData?.email || localStorage.getItem('pendingEmail')) &&
        (!emailConfirmationToken || !email);
      
      const invitationToken = parsedProfilingData?.invitationToken || null;
      const isInvitedClubAdmin = parsedProfilingData?.userType === 'club_admin' && Boolean(invitationToken);

      const emailToUse = isDirectPlayerRegistration
        ? (parsedProfilingData?.email || localStorage.getItem('pendingEmail'))
        : (email || parsedProfilingData?.adminEmail || parsedProfilingData?.email);

      if (!emailConfirmationToken && !isDirectPlayerRegistration && !isInvitedClubAdmin) {
        console.log('❌ Missing token and not direct player registration:', { token: !!emailConfirmationToken });
        throw new Error('Email confirmation required. Please start the registration process again.');
      }
      if (!emailToUse) {
        console.log('❌ No email available');
        throw new Error('Email is required. Please complete the profiling form with your email first.');
      }

      if (
        parsedProfilingData?.userType === 'club_admin' ||
        parsedProfilingData?.userType === 'committee' ||
        parsedProfilingData?.inviteRole === 'committee'
      ) {
        const { nextPath } = await completeClubAdminRegistration({
          password: formData.password,
          profiling: parsedProfilingData,
          i18n,
          loadPermissions,
          refreshBrand,
        });
        navigate(nextPath);
        return;
      }

      // Check if this is a dual role scenario
      const isDualRole = parsedProfilingData?.isDualRole || false;
      
      // Check for automatic dual role data from PlayerProfiling
      const dualRoleData = localStorage.getItem('dualRoleData');
      let parsedDualRoleData = null;
      
      if (dualRoleData) {
        try {
          parsedDualRoleData = JSON.parse(dualRoleData);
          console.log('🔄 Auto dual role data found:', parsedDualRoleData);
        } catch (error) {
          console.log('❌ Error parsing dual role data:', error);
        }
      }
      
      let newUser;
      
      if (isDualRole || parsedDualRoleData) {
        // For dual role, we need to find the existing user and create the additional role
        console.log('🔄 Dual role creation detected');
        
        let existingUser;
        
        if (parsedDualRoleData) {
          // Use the existing user data from automatic dual role detection
          existingUser = parsedDualRoleData.existingUser;
          console.log('👤 Using existing user from dual role data:', existingUser);
        } else {
          // Find the existing user by email (manual dual role scenario)
          const existingUserResponse = await axios.post(`${ip_back}api/user/getUserByEmail`, { email });
          
          if (existingUserResponse.data.status !== 'success') {
            throw new Error('Could not find existing user for dual role creation');
          }
          
          existingUser = existingUserResponse.data.data;
          console.log('👤 Found existing user:', existingUser);
        }
        
        // Determine what role to add
        // Note: type_user_id: 1=Super Admin, 2=Admin (Club Admin), 3=Coach, 4=Player
        const isExistingAdmin = existingUser.type_user_id === 2;
        const isExistingPlayer = existingUser.type_user_id === 4;
        
        if ((isExistingAdmin && parsedProfilingData?.userType === 'club_admin') || 
            (parsedDualRoleData && parsedDualRoleData.newRole === 'player')) {
          // User is already an admin, trying to add player role
          console.log('🔄 Adding player role to existing admin');
          
          // For automatic dual role, we need to get player data from the profiling form
          let playerData = {};
          if (parsedDualRoleData) {
            // This is automatic dual role - we need to get player data from the current form
            // Since this is coming from player profiling, we'll use default values or get from form
            playerData = {
              grip: null, // Will be set later in player profiling
              hand: null, // Will be set later in player profiling
              level_id: null // Will be set later in player profiling
            };
          } else {
            // Manual dual role from club admin profiling
            playerData = {
              grip: parsedProfilingData.playerGrip,
              hand: parsedProfilingData.playerHand,
              level_id: parsedProfilingData.levelId
            };
          }
          
          const dualRoleData = {
            user_id: existingUser.user_id,
            club_id: parsedProfilingData?.selectedClub?.club_id || existingUser.club_id,
            playerData: playerData
          };
          
          const dualRoleResponse = await axios.post(`${ip_back}api/club/createDualRoleAdminPlayer`, dualRoleData);
          
          if (dualRoleResponse.data.status !== 'success') {
            throw new Error(dualRoleResponse.data.message || 'Dual role creation failed');
          }
          
          newUser = dualRoleResponse.data.data.user;
          console.log('✅ Dual role created successfully');
          
        } else if (isExistingPlayer && parsedProfilingData?.userType === 'club_admin') {
          // User is already a player, trying to add admin role
          console.log('🔄 Adding admin role to existing player');
          
          const adminData = {
            user_id: existingUser.user_id,
            club_id: parsedProfilingData.selectedClub?.club_id,
            admin_description: "Club Administrator",
            adminPosition: parsedProfilingData.adminPosition,
            clubWebsite: parsedProfilingData.clubWebsite,
            invitationToken: parsedProfilingData.invitationToken || undefined,
            adminProofUrl: parsedProfilingData.adminProofUrl || undefined,
            scforh: parsedProfilingData.scforh || undefined,
          };
          
          const adminResponse = await axios.post(`${ip_back}api/club/createAdmin`, adminData);
          
          if (adminResponse.data.status !== 'success') {
            throw new Error(adminResponse.data.message || 'Admin role creation failed');
          }
          
          newUser = adminResponse.data.data.user;
          console.log('✅ Admin role added to existing player');
        } else {
          throw new Error('Invalid dual role scenario');
        }
        
      } else {
        // Regular user creation
        // Get club_id from multiple possible sources (selectedClub.club_id, clubId, or club)
        const clubId = parsedProfilingData?.selectedClub?.club_id 
          || parsedProfilingData?.clubId 
          || parsedProfilingData?.club 
          || null;
        
        console.log('🔍 Extracting clubId from profiling data:', {
          selectedClub_club_id: parsedProfilingData?.selectedClub?.club_id,
          clubId: parsedProfilingData?.clubId,
          club: parsedProfilingData?.club,
          finalClubId: clubId
        });
        
        const registerData = {
          email: emailToUse,
          password: formData.password,
          firstName: parsedProfilingData?.userType === 'club_admin' 
            ? parsedProfilingData?.adminFirstName 
            : parsedProfilingData?.firstName || emailToUse.split('@')[0],
          lastName: parsedProfilingData?.userType === 'club_admin' 
            ? parsedProfilingData?.adminLastName 
            : parsedProfilingData?.lastName || 'User',
          sex: parsedProfilingData?.userType === 'club_admin' 
            ? parsedProfilingData?.adminGender 
            : parsedProfilingData?.gender || 'male',
          // Only include dateBorn if provided; otherwise omit it
          ...(parsedProfilingData?.userType === 'club_admin' 
            ? (parsedProfilingData?.adminDateOfBirth ? { dateBorn: new Date(parsedProfilingData.adminDateOfBirth) } : {})
            : (parsedProfilingData?.dateOfBirth ? { dateBorn: new Date(parsedProfilingData.dateOfBirth) } : {})),
          typeUser_id: parsedProfilingData?.userType === 'club_admin' ? 2 : 4, // Club admin (2) or player (4) type
          confirmation_token: isInvitedClubAdmin
            ? invitationToken
            : (isDirectPlayerRegistration ? 'direct_registration' : emailConfirmationToken),
          invitation_token: invitationToken || undefined,
          adminPosition: parsedProfilingData?.adminPosition, // Add admin position for club admins
          club_id: clubId // Include club_id if available for player search
        };
        
        console.log('🚀 Register data being sent:', registerData);
        console.log('🚀 Type user ID:', registerData.typeUser_id);
        console.log('🚀 Club ID:', clubId);
        
        try {
          const registerResponse = await axios.post(`${ip_back}api/user/registerUser`, registerData);
          if (registerResponse.data.status !== 'success') {
            throw new Error(registerResponse.data.message || 'Registration failed');
          }
          newUser = registerResponse.data.data;
        } catch (registerError) {
          const errMsg = registerError.response?.data?.message || registerError.message || '';
          const accountExists = errMsg.toLowerCase().includes('already registered');
          if (accountExists && parsedProfilingData?.userType !== 'club_admin') {
            console.log('👤 Account already exists — signing in to finish profile');
            const loginResponse = await axios.post(`${ip_back}api/user/login`, {
              email: emailToUse,
              password: formData.password,
            });
            if (loginResponse.data.status !== 'success') {
              throw new Error('This email is already registered. Please log in with your password.');
            }
            newUser = loginResponse.data.data;
          } else {
            throw registerError;
          }
        }
      }
      
      // Handle club creation for club admins
      if (parsedProfilingData?.userType === 'club_admin') {
        console.log('🏢 Creating club for club admin...');
        
        let clubId;
        
        console.log('🔍 Checking selectedClub:', parsedProfilingData.selectedClub);
        console.log('🔍 selectedClub exists:', !!parsedProfilingData.selectedClub);
        console.log('🔍 selectedClub.club_id exists:', !!(parsedProfilingData.selectedClub && parsedProfilingData.selectedClub.club_id));
        
        if (parsedProfilingData.selectedClub && parsedProfilingData.selectedClub.club_id) {
          // Club already exists (selected from Luxembourg clubs), just create admin relationship
          clubId = parsedProfilingData.selectedClub.club_id;
          console.log('🏢 Using existing club:', parsedProfilingData.selectedClub.club_name, 'ID:', clubId);
          
          // Create admin relationship only
          const adminData = {
            user_id: newUser.user_id,
            club_id: clubId,
            admin_description: "Club Administrator",
            adminPosition: parsedProfilingData.adminPosition,
            // Pass website so backend can update club and mark active
            clubWebsite: parsedProfilingData.clubWebsite,
            invitationToken: parsedProfilingData.invitationToken || undefined,
            adminProofUrl: parsedProfilingData.adminProofUrl || undefined,
            scforh: parsedProfilingData.scforh || undefined,
          };
          
          console.log('👤 Creating admin relationship:', adminData);
          const adminResponse = await axios.post(`${ip_back}api/club/createAdmin`, adminData);
          console.log('👤 Admin relationship response:', adminResponse.data);
          
          if (adminResponse.data.status !== 'success') {
            throw new Error(adminResponse.data.message || 'Admin relationship creation failed');
          }
          
          console.log('✅ Admin relationship created successfully');
          
          // Update newUser with the updated user data from createAdmin response
          if (adminResponse.data.data && adminResponse.data.data.user) {
            const updatedUserFromAdmin = adminResponse.data.data.user;
            console.log('🔄 Updating user data from createAdmin response:', updatedUserFromAdmin);
            newUser = updatedUserFromAdmin;
          }
          
        } else {
          // Create new club
          const clubData = {
            club_name: parsedProfilingData.clubName,
            club_country: parsedProfilingData.clubCountryCode || parsedProfilingData.clubCountry,
            club_city: parsedProfilingData.clubCity,
            club_address: parsedProfilingData.clubAddress,
            club_postal_code: parsedProfilingData.clubPostalCode,
            club_phone: parsedProfilingData.clubPhone,
            club_website: parsedProfilingData.clubWebsite,
            club_description: parsedProfilingData.clubDescription,
            club_founded_year: parsedProfilingData.clubFoundedYear,
            club_number_of_members: parsedProfilingData.clubNumberOfMembers,
            club_number_of_tables: parsedProfilingData.clubNumberOfTables,
            club_facilities: parsedProfilingData.clubFacilities
          };

          console.log('🏢 Creating new club:', clubData);
          
          const clubResponse = await axios.post(`${ip_back}api/club/createClub`, clubData);
          console.log('🏢 Club creation response:', clubResponse.data);
          
          if (clubResponse.data.status !== 'success') {
            throw new Error(clubResponse.data.message || 'Club creation failed');
          }

          clubId = clubResponse.data.data.club.club_id;
          console.log('✅ New club created successfully with ID:', clubId);
          
          // Create admin record linking the user to the club
          const adminData = {
            user_id: newUser.user_id,
            club_id: clubId,
            admin_description: "Club Administrator",
            adminPosition: parsedProfilingData?.adminPosition,
            clubWebsite: parsedProfilingData?.clubWebsite, // Pass clubWebsite for update
            invitationToken: parsedProfilingData?.invitationToken || undefined,
            adminProofUrl: parsedProfilingData?.adminProofUrl || undefined,
            scforh: parsedProfilingData?.scforh || undefined,
          };
          
          console.log('🏢 Creating admin record:', adminData);
          
          const adminResponse = await axios.post(`${ip_back}api/club/createAdmin`, adminData);
          console.log('🏢 Admin creation response:', adminResponse.data);
          
          if (adminResponse.data.status !== 'success') {
            throw new Error(adminResponse.data.message || 'Admin creation failed');
          }
          
          console.log('✅ Admin record created successfully');
          
          // Update newUser with the updated user data from createAdmin response
          if (adminResponse.data.data && adminResponse.data.data.user) {
            const updatedUserFromAdmin = adminResponse.data.data.user;
            console.log('🔄 Updating user data from createAdmin response:', updatedUserFromAdmin);
            newUser = updatedUserFromAdmin;
          }
        }
        
        // Clean up profiling data
        localStorage.removeItem('profilingFormData');
        
        // Clean up dual role data if it exists
        if (parsedDualRoleData) {
          localStorage.removeItem('dualRoleData');
          console.log('🧹 Cleaned up dual role data');
        }

        const clubName =
          parsedProfilingData.clubName ||
          parsedProfilingData.selectedClub?.club_name ||
          newUser?.club_name ||
          newUser?.club_details?.club_name ||
          null;
        newUser = mergeClubAdminUser(
          {
            ...newUser,
            club_id: clubId || newUser?.club_id,
            club_name: clubName,
          },
          parsedProfilingData,
          clubId
        );
        applyClubAdminUiLanguage(i18n, newUser);
        
        // Save user data
        localStorage.setItem('user', JSON.stringify(newUser));
        localStorage.setItem('token', newUser.user_token);
        localStorage.setItem(HOME_ACTIVE_ROLE_KEY, 'admin');
        await recordPrivacyAndTermsAcceptance();
        
        // Clean up temporary data
        localStorage.removeItem('emailConfirmationToken');
        localStorage.removeItem('pendingEmail');
        
        console.log('✅ Club admin registration completed successfully');
        console.log('🏢 Club ID:', clubId);
        console.log('👤 User ID:', newUser.user_id);
        
        // Load permissions for club admin
        if (newUser.permissions) {
          loadPermissions(newUser.permissions);
          console.log('✅ Permissions loaded for club admin:', newUser.permissions);
        } else {
          console.log('⚠️ No permissions found for club admin');
        }
        
        // Check if this is automatic dual role (admin adding player role)
        if (parsedDualRoleData && parsedDualRoleData.newRole === 'player') {
          // This is automatic dual role - continue with player profiling
          console.log('🔄 Automatic dual role: continuing with player profiling');
          navigate('/profiling');
          return;
        } else {
          await syncBrandAfterAuth(newUser, refreshBrand, { clubIdHint: clubId });
          navigate('/home');
          return;
        }
      }
      
      // Save user data
      localStorage.setItem('user', JSON.stringify(newUser));
      localStorage.setItem('token', newUser.user_token);
      await recordPrivacyAndTermsAcceptance();
      
      // Load permissions for the new user
      if (newUser.permissions) {
        loadPermissions(newUser.permissions);
        console.log('✅ Permissions loaded for new user:', newUser.permissions);
      } else {
        console.log('⚠️ No permissions found for new user');
      }
      
      // Clean up temporary data
      localStorage.removeItem('emailConfirmationToken');
      localStorage.removeItem('pendingEmail');
      
      // For new user registration, complete the profile with the saved form data
      console.log('🆕 New user registered, completing profile with saved form data');
      
      // Prefer in-memory profiling data from this submit; fall back to localStorage
      const savedFormData = parsedProfilingData
        ? JSON.stringify(parsedProfilingData)
        : localStorage.getItem('profilingFormData');
      if (savedFormData) {
        try {
          const parsedFormData = typeof savedFormData === 'string'
            ? JSON.parse(savedFormData)
            : savedFormData;
          console.log('📋 Found saved form data, completing profile:', parsedFormData);
          
          // Complete the profile with the new user and saved form data
          const profileData = {
            userId: newUser.user_id,
            mainPurposeId: parsedFormData.mainPurposeId,
            experienceLevelId: parsedFormData.experienceLevelId,
            styleId: parsedFormData.styleId,
            primaryFocusId: parsedFormData.primaryFocusId,
            secondaryFocusIds: parsedFormData.secondaryFocusIds,
            primaryFocusCustomText: parsedFormData.primaryFocusCustomText,
            secondaryFocusCustomText: parsedFormData.secondaryFocusCustomText,
            levelId: parsedFormData.levelId,
            customLevel: parsedFormData.customLevel,
            country: parsedFormData.country,
            clubId: parsedFormData.clubId,
            grip: parsedFormData.grip,
            hand: parsedFormData.hand,
            gender: parsedFormData.gender,
            dateOfBirth: parsedFormData.dateOfBirth,
            firstName: parsedFormData.firstName,
            lastName: parsedFormData.lastName
          };
          
          console.log('📤 Completing profile for new user:', profileData);
          
          // Validate required fields before sending
          const isCompetitionPlayer = parsedFormData.mainPurposeId === 3;
          const baseRequiredFields = ['userId', 'mainPurposeId', 'styleId', 'primaryFocusId', 'grip', 'hand'];
          const requiredFields = isCompetitionPlayer 
            ? [...baseRequiredFields, 'country'] // Competition players need country
            : [...baseRequiredFields, 'experienceLevelId']; // Health/Recreational players need experienceLevelId, not levelId
          
          // For competition players, validate level and club based on country
          if (isCompetitionPlayer) {
            if (parsedFormData.country === 'LU') {
              if (!parsedFormData.levelId) {
                throw new Error('Missing required fields: levelId (Luxembourg classement level required)');
              }
              if (!parsedFormData.clubId) {
                throw new Error('Missing required fields: clubId (Luxembourg club selection required)');
              }
            }
            if (parsedFormData.country !== 'LU' && !parsedFormData.customLevel) {
              throw new Error('Missing required fields: customLevel (classement level for your country required)');
            }
          } else if (!parsedFormData.clubId) {
            throw new Error('Missing required fields: clubId (club selection required)');
          }
          
          const missingFields = requiredFields.filter(field => !profileData[field]);
          if (missingFields.length > 0) {
            console.error('❌ Missing required fields:', missingFields);
            throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
          }
          
          // Complete the profile
          const profileResponse = await axios.post(`${ip_back}api/profile/complete`, profileData);
          console.log('📤 Profile completion response:', profileResponse);
          
          if (profileResponse.data.success) {
            // Update user data with complete profile information
            let prevUser = {};
            try {
              prevUser = JSON.parse(localStorage.getItem('user') || '{}') || {};
            } catch {
              prevUser = {};
            }
            const completeUserData = {
              ...prevUser,
              ...profileResponse.data.data,
              isProfileCompleted: true,
              user_token: prevUser.user_token || profileResponse.data.data?.user_token,
              token: prevUser.token || profileResponse.data.data?.token,
              all_roles: profileResponse.data.data?.all_roles || prevUser.all_roles,
              all_role_names: profileResponse.data.data?.all_role_names || prevUser.all_role_names,
              hasCoachRole: profileResponse.data.data?.hasCoachRole ?? prevUser.hasCoachRole,
              hasAdminRole: profileResponse.data.data?.hasAdminRole ?? prevUser.hasAdminRole,
              hasPlayerRole: true,
              type_user_id: profileResponse.data.data?.type_user_id ?? prevUser.type_user_id,
            };
            
            localStorage.setItem('user', JSON.stringify(completeUserData));
            console.log('🆕 Complete user data saved:', completeUserData);
            
            // Clean up form data from localStorage since profile is complete
            localStorage.removeItem('profilingFormData');
            console.log('🧹 Cleaned up form data from localStorage');
            await syncBrandAfterAuth(completeUserData, refreshBrand, {
              clubIdHint: parsedFormData.clubId,
            });
            
            // Redirect to appropriate dashboard
            if (newUser.type_user_id === 1) {
              console.log('👑 Redirecting admin to /Dashboard');
              navigate('/Dashboard');
            } else {
              console.log('🎾 Redirecting user to /home');
              navigate('/home');
            }
          } else {
            throw new Error('Profile completion failed');
          }
          
        } catch (error) {
          console.error('Profile completion error:', error);
          setErrors({ 
            general: error.response?.data?.message || error.message || 'Profile completion failed. Please try again.' 
          });
        }
      } else {
        // No profiling form data found - check if profile is already completed
        console.log('❌ No saved form data found, checking if profile is already completed...');
        
        // Check if user already has a completed profile by making an API call
        try {
          const userProfileResponse = await axios.get(`${ip_back}api/profile/user/${newUser.user_id}`);
          if (userProfileResponse.data.success && userProfileResponse.data.data) {
            const profileData = userProfileResponse.data.data;
            // Check if profile is already completed
            if (profileData.isProfileCompleted || profileData.main_purpose_id) {
              console.log('✅ Profile already completed, redirecting to /home');
              // Profile is already completed, update localStorage and redirect to home
              const completeUserData = {
                ...newUser,
                isProfileCompleted: true,
                ...profileData
              };
              localStorage.setItem('user', JSON.stringify(completeUserData));
              await syncBrandAfterAuth(completeUserData, refreshBrand, {
                clubIdHint: profileData.club_id,
              });
              
              // Redirect to appropriate dashboard
              if (newUser.type_user_id === 1) {
                navigate('/Dashboard');
              } else {
                navigate('/home');
              }
              return;
            }
          }
        } catch (error) {
          console.log('⚠️ Could not check profile status:', error.message);
        }
        
        // Account was created but profiling data was lost — don't loop back to profiling
        console.log('❌ Profile data missing after registration');
        throw new Error(
          'Your account was created but profile data could not be saved. ' +
          'Please log in and complete your profile, or restart registration.'
        );
      }
      
    } catch (error) {
      console.error('Registration error:', error);
      
      // Handle specific error types
      let errorMessage = 'Registration failed. Please try again.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Check for token-related errors
      if (errorMessage.includes('confirmation token') || 
          errorMessage.includes('Invalid') || 
          errorMessage.includes('expired')) {
        errorMessage = 'Invalid or expired confirmation token. Please restart the registration process.';
      }
      
      setErrors({ 
        general: errorMessage
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!emailConfirmed) {
    return (
      <div className="profiling-container">
        <div className="profiling-card">
          <div className="profiling-header">
            <div className="profiling-logo">
              <div className="loading-spinner"></div>
            </div>
            <h1>{t('verifyingEmail')}</h1>
            <p>{t('pleaseWaitVerifyEmail')}</p>
            <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '1rem' }}>
              {t('ifTakesTooLong')}
            </p>
            <button 
              onClick={() => navigate('/register')} 
              className="next-button"
            >
              {t('backToRegistration')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profiling-container">
      <div className="profiling-card">
        <div className="profiling-header">
          <div className="profiling-logo">
            <div className="success-icon">✅</div>
          </div>
          <h1>{userType === 'club_admin' ? 'Club Admin Registration' : t('emailConfirmed')}</h1>
          <p>{userType === 'club_admin' ? 'Create your club admin password to complete registration' : t('welcomeCreatePassword')}</p>
          {userEmail && <p className="email-display">{t('emailDisplay')} {userEmail}</p>}
        </div>
        
        <form onSubmit={handleSubmit} className="profiling-form">
          {errors.general && (
            <div className="error-message general-error">
              {errors.general}
              
              {/* Always show retry button when there's an error */}
              <div style={{ marginTop: '1rem' }}>
                <button 
                  type="button"
                  onClick={() => {
                    console.log('🔄 Restarting registration process (error retry)...');
                    console.log('📋 Current localStorage keys:', Object.keys(localStorage));
                    console.log('📋 Current sessionStorage keys:', Object.keys(sessionStorage));
                    console.log('📋 Error message:', errors.general);
                    
                    // Clear everything - same as working debug button
                    localStorage.clear();
                    sessionStorage.clear();
                    
                    console.log('✅ All storage cleared');
                    window.location.href = '/register?fresh=true';
                  }}
                  className="retry-button"
                >
                  {t('restartRegistrationProcess')}
                </button>
              </div>
            </div>
          )}
          
          <div className="input-field">
            <label htmlFor="password">{t('password')}</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className={errors.password ? 'error' : ''}
              placeholder={t('enterPasswordMin6')}
              disabled={isLoading}
            />
            {errors.password && <span className="error-text">{errors.password}</span>}
          </div>
          
          <div className="input-field">
            <label htmlFor="passwordConfirmation">{t('confirmPassword')}</label>
            <input
              type="password"
              id="passwordConfirmation"
              name="passwordConfirmation"
              value={formData.passwordConfirmation}
              onChange={handleInputChange}
              className={errors.passwordConfirmation ? 'error' : ''}
              placeholder={t('confirmYourPassword')}
              disabled={isLoading}
            />
            {errors.passwordConfirmation && <span className="error-text">{errors.passwordConfirmation}</span>}
          </div>

          <LegalAcceptCheckbox
            checked={formData.legalAccepted}
            onChange={(v) => {
              setFormData((prev) => ({ ...prev, legalAccepted: v }));
              if (errors.legalAccepted) {
                setErrors((prev) => ({ ...prev, legalAccepted: '' }));
              }
            }}
          />
          {errors.legalAccepted && (
            <span className="error-text">{errors.legalAccepted}</span>
          )}
          
          <div className="profiling-navigation">
            <button 
              type="submit" 
              className="next-button"
              disabled={isLoading}
            >
              {isLoading ? (userType === 'club_admin' ? 'Creating Club Admin Account...' : t('creatingAccount')) : (userType === 'club_admin' ? 'Complete Club Admin Registration' : t('completeRegistration'))}
            </button>
          </div>
        </form>
        
        {/* Always visible retry button */}
        <div className="profiling-footer">
          <button 
            onClick={() => {
              localStorage.clear();
              sessionStorage.clear();
              window.location.href = '/register?fresh=true';
            }}
            className="secondary-button"
          >
            {t('restartRegistrationProcessAlways')}
          </button>
          
          <p>{userType === 'club_admin' ? 'Already have a club admin account?' : t('alreadyHaveAccount')} <button onClick={() => navigate('/login')} className="link-button">{t('signIn')}</button></p>
          <LegalFooter />
        </div>
      </div>
    </div>
  );
};

export default PasswordRegistration;
